import { RadioGroup } from '@headlessui/react';
import {
	BuildingOfficeIcon,
	CheckCircleIcon,
	UserIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
	addContact,
	deleteContact,
	getContact,
	getContacts,
	updateContact,
} from '../../api/contacts';

import { classNames } from '../../components/helpers/functions';

import { Field, FormRow, Label } from '../../components/Form/tailwindForm';
import Button from '../../components/Button';
import { Base, Content } from '../../components/Shell/Content';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import { Input } from '../../components/Form';
import { PageHeading } from '../../components/Shell/Content';
import { SelectCreatable } from '../../components/SelectCreatable_old';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';

const titleOptions = [
	{ label: 'Field Engineer', key: 'field-engineer' },
	{ label: 'Support', key: 'support' },
	{ label: 'Service Manager', key: 'service-manager' },
	{ label: 'Sales', key: 'sales' },
	{ label: 'Finance', key: 'finance' },
	{ label: 'Executive', key: 'executive' },
];

export default () => {
	const navigate = useNavigate();
	const { id } = useParams();

	// Set States
	const [selectedType, setSelectedType] = useState('person');
	const [form, setForm] = useState({});

	// Async Calls
	const {
		value: contact,
		execute: getSingleContact,
		status: isLoadingContact,
	} = useAsync(getContact, {
		immediate: false,
	});

	const {
		execute: onAdd,
		error,
		status,
	} = useAsync(addContact, {
		immediate: false,
	});

	const { value: contacts, status: isLoadingContacts } = useAsync(getContacts);

	const { execute: onDeleteContact, status: isDeletingContact } = useAsync(
		deleteContact,
		{
			immediate: false,
		}
	);

	const { execute: onUpdateContact, status: isUpdating } = useAsync(
		updateContact,
		{ immediate: false }
	);

	const { setOpen: setDeletingModalOpen, setClose: setDeletingModalClose } =
		useModal('deleteModal');

	useEffect(() => {
		if (id) {
			getSingleContact({ id });
		}
	}, [id]);

	useEffect(() => {
		if (contact) {
			setForm({
				type: contact.type,
				email: contact.email,
				firstName: contact.firstName,
				lastName: contact.lastName,
				phoneNumber: contact.phoneNumber,
				location: contact.location,
				title: { label: contact.title, value: contact.title },
				company: {
					label: contact.company?.companyName,
					value: contact?.company?.id,
				},
				companyName: contact.companyName,
			});

			setSelectedType(contact.type);
		}
	}, [contact]);

	const handleSetForm = (key, value) => {
		let temp = form;

		if (value === '' && !isEditing) {
			delete temp[key];
		} else {
			temp[key] = value;
		}

		setForm(temp);
	};

	const isEditing = !!id;
	const headingLabel = isEditing ? 'Edit Contact' : 'New Contact';
	const hasError = error;
	const isLoading = status === 'pending';

	const uniqueCompanies = [
		...new Map(
			contacts?.rows
				?.filter((c) => c.type === 'company')
				?.map((p) => ({
					label: p.companyName,
					value: p.id,
				}))
				.map((item) => [item['label'], item])
		).values(),
	];

	return (
		<Base className="md:px-36">
			<div className="md:px-36">
				<PageHeading
					onBack={() => navigate(`/manage/directory${id ? `/${id}` : ''}`)}
				>
					{headingLabel}
				</PageHeading>
				<Content>
					{isLoadingContact === 'pending' || isLoadingContacts === 'pending' ? (
						<LoadingSkeleton className="mt-4" />
					) : (
						<>
							{hasError ? (
								<div
									className="p-4 mb-4 text-xs text-red-700 bg-red-100 rounded-lg"
									role="alert"
								>
									{hasError?.message || 'Uh oh, something went wrong'}
								</div>
							) : null}

							<div className="mx-auto mt-6 max-w-5xl px-4 sm:px-6 lg:px-8">
								{!isEditing && (
									<div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
										<div className="sm:col-span-2">
											<FormRow>
												<Label>Contact type:</Label>
												<Field>
													<RadioGroup
														value={selectedType}
														onChange={(e) => {
															setSelectedType(e);
															handleSetForm('type', e);
														}}
													>
														<div className="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-3 sm:gap-x-4">
															{[
																{ label: 'Person', value: 'person' },
																{ label: 'Company', value: 'company' },
															].map((type) => (
																<RadioGroup.Option
																	key={type.value}
																	value={type.value}
																	className={({ checked, active }) =>
																		classNames(
																			checked
																				? 'border-transparent'
																				: 'border-gray-300',
																			active
																				? 'border-indigo-500 ring-2 ring-indigo-500'
																				: '',
																			'relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none'
																		)
																	}
																>
																	{({ checked, active }) => (
																		<>
																			{type.value === 'person' && (
																				<UserIcon
																					className={classNames(
																						active ? 'text-indigo-600' : '',
																						'h-5 w-5 text-gray-600 mr-2'
																					)}
																				/>
																			)}
																			{type.value === 'company' && (
																				<BuildingOfficeIcon
																					className={classNames(
																						active ? 'text-indigo-600' : '',
																						'h-5 w-5 text-gray-600 mr-2'
																					)}
																				/>
																			)}
																			<span className="flex flex-1">
																				<span className="flex flex-col">
																					<RadioGroup.Label
																						as="span"
																						className="block text-sm font-medium text-gray-900"
																					>
																						{type.label}
																					</RadioGroup.Label>
																				</span>
																			</span>
																			<CheckCircleIcon
																				className={classNames(
																					!checked ? 'invisible' : '',
																					'h-5 w-5 text-indigo-600'
																				)}
																				aria-hidden="true"
																			/>
																			<span
																				className={classNames(
																					active ? 'border' : 'border-2',
																					checked
																						? 'border-indigo-500'
																						: 'border-transparent',
																					'pointer-events-none absolute -inset-px rounded-lg'
																				)}
																				aria-hidden="true"
																			/>
																		</>
																	)}
																</RadioGroup.Option>
															))}
														</div>
													</RadioGroup>
												</Field>
											</FormRow>
										</div>
									</div>
								)}
								{selectedType && (
									<div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 mt-4">
										{selectedType === 'company' && (
											<div className="sm:col-span-2">
												<FormRow>
													<Label>Company Name*</Label>
													<Field>
														<Input
															placeholder={'Company Name'}
															onChange={(e) =>
																handleSetForm('companyName', e.target.value)
															}
															defaultValue={form.companyName}
														/>
													</Field>
												</FormRow>
											</div>
										)}
										<div
											className={
												selectedType === 'person'
													? 'sm:col-span-2'
													: 'sm:col-span-1'
											}
										>
											<FormRow>
												<Label>Email*</Label>
												<Field>
													<Input
														placeholder={'Email'}
														onChange={(e) =>
															handleSetForm('email', e.target.value)
														}
														autoFocus
														defaultValue={form.email}
													/>
												</Field>
											</FormRow>
										</div>

										{selectedType === 'person' && (
											<>
												<div className="sm:col-span-1">
													<FormRow>
														<Label>First Name</Label>
														<Field>
															<Input
																placeholder={'First Name'}
																onChange={(e) =>
																	handleSetForm('firstName', e.target.value)
																}
																defaultValue={form.firstName}
															/>
														</Field>
													</FormRow>
												</div>
												<div className="sm:col-span-1">
													<FormRow>
														<Label>Last Name</Label>
														<Field>
															<Input
																placeholder={'Last Name'}
																onChange={(e) =>
																	handleSetForm('lastName', e.target.value)
																}
																defaultValue={form.lastName}
															/>
														</Field>
													</FormRow>
												</div>
											</>
										)}

										<div className="sm:col-span-1">
											<FormRow>
												<Label>Phone Number</Label>
												<Field>
													<Input
														placeholder={'Phone Number'}
														onChange={(e) =>
															handleSetForm('phoneNumber', e.target.value)
														}
														defaultValue={form.phoneNumber}
													/>
												</Field>
											</FormRow>
										</div>
										<div className="sm:col-span-1">
											<FormRow>
												<Label>Location</Label>
												<Field>
													<Input
														placeholder={'Location'}
														onChange={(e) =>
															handleSetForm('location', e.target.value)
														}
														defaultValue={form.location}
													/>
												</Field>
											</FormRow>
										</div>

										{selectedType === 'person' && (
											<>
												<div className="sm:col-span-1">
													<FormRow>
														<Label>Title</Label>
														<Field>
															<SelectCreatable
																options={titleOptions || []}
																isClearable
																isSearchable
																placeholder="Select or create title"
																onChange={(e) => {
																	handleSetForm('title', e || null);
																}}
																style={{ width: '100%' }}
																defaultValue={titleOptions.find(
																	(t) => t.label === form?.title?.label
																)}
															/>
														</Field>
													</FormRow>
												</div>

												<div className="sm:col-span-1">
													<FormRow>
														<Label>Company</Label>
														<Field>
															<SelectCreatable
																options={uniqueCompanies || []}
																placeholder={'Select or create company'}
																isSearchable
																isClearable
																onChange={(e) => {
																	handleSetForm('company', e);
																}}
																defaultValue={uniqueCompanies.find((c) => {
																	return c.value === form?.company?.value;
																})}
																getOptionValue={(option) => option.value}
															/>
														</Field>
													</FormRow>
												</div>
											</>
										)}
									</div>
								)}
							</div>

							<FormRow className="pt-4" alignRight>
								{isEditing && (
									<Button
										type="danger"
										onClick={() =>
											setDeletingModalOpen({
												title: 'Are you sure you want to delete this contact?',
												confirmText: 'Yes, delete',
												onConfirm: () =>
													onDeleteContact(contact?.id, () => {
														setDeletingModalClose();
														navigate('/manage/directory');
													}),
											})
										}
										className="text-red-400 hover:text-red-600 border-red-200"
									>
										Delete
									</Button>
								)}
								<Button
									onClick={() => {
										const action = isEditing ? onUpdateContact : onAdd;
										action(
											configurePayload({ ...form, type: selectedType, id }),
											(newItem) => {
												navigate(
													`/manage/directory/${newItem?.id || id || ''}`
												);
											}
										);
									}}
								>
									{isLoading
										? isEditing
											? 'Editing...'
											: 'Adding...'
										: isEditing
										? 'Edit contact'
										: 'Add contact'}
								</Button>
							</FormRow>
						</>
					)}
				</Content>
			</div>
		</Base>
	);
};

const configurePayload = ({ title, company, ...rest }) => {
	return {
		...rest,
		title: title?.label || null,
		...(company?.__isNew__
			? { createNewCompany: company?.label }
			: company?.value
			? { companyId: company?.value }
			: { companyId: null }),
	};
};
